import { type ReactNode } from 'react'
import { FormattedMessage, type IntlShape } from 'react-intl'

import {
  OfferConditionTypeEnumV2,
  OfferDiscountTypeEnumV2,
  type SellerPromotionV2,
} from '@redteclab/api/clients/bully'

import { type TENANT } from '../../../tenant'

import {
  getFormattedPromotionDiscountedAmountOrPercentage,
  getFormattedPromotionDiscountValueWithCurrency,
} from './productSellerPromotionFormatter'
import { ProductSellerPromotionInfoBannerMovMessage } from './ProductSellerPromotionInfoBannerMovMessage'

const renderMinimumOrderValueMessage = (
  sellerPromotion: SellerPromotionV2,
  intl: IntlShape,
  sellerName?: string,
): ReactNode => (
  <FormattedMessage
    id="seller.promotion.product.banner.info"
    values={{
      formattedDiscountAmount:
        getFormattedPromotionDiscountedAmountOrPercentage(
          sellerPromotion,
          intl,
        ),
      formattedMovAmount: getFormattedPromotionDiscountValueWithCurrency(
        sellerPromotion.conditionValue,
        intl,
      ),
      sellerName,
    }}
  />
)

const renderMinQuantityMessage = (
  sellerPromotion: SellerPromotionV2,
  intl: IntlShape,
  sellerName?: string,
): ReactNode => {
  switch (sellerPromotion.discountType) {
    case OfferDiscountTypeEnumV2.FreeItems: {
      return (
        <FormattedMessage
          id="seller.promotion.product.banner.info.minQuantity.freeItems"
          values={{
            sellerName,
            targetItemCount: sellerPromotion.conditionValue,
          }}
        />
      )
    }
    case OfferDiscountTypeEnumV2.TotalAmount:
    case OfferDiscountTypeEnumV2.TotalPercent: {
      return (
        <FormattedMessage
          id="seller.promotion.product.banner.info.minQuantity.total"
          values={{
            formattedDiscountAmount:
              getFormattedPromotionDiscountedAmountOrPercentage(
                sellerPromotion,
                intl,
              ),
            sellerName,
            targetItemCount: sellerPromotion.conditionValue,
          }}
        />
      )
    }
    default: {
      return null
    }
  }
}

const renderMinQuantitySameItemMessage = ({
  footnoteNumber,
  intl,
  sellerName,
  sellerPromotion,
}: {
  footnoteNumber?: number
  intl: IntlShape
  sellerName?: string
  sellerPromotion: SellerPromotionV2
}): ReactNode => {
  switch (sellerPromotion.discountType) {
    case OfferDiscountTypeEnumV2.FreeItems: {
      return (
        <FormattedMessage
          id="seller.promotion.product.banner.info.minSameQuantity.freeItems"
          values={{
            payableItemCount:
              sellerPromotion.conditionValue - sellerPromotion.discountValue,
            sellerName,
            sup: footnoteNumber ? <sup>{footnoteNumber}</sup> : null,
            targetItemCount: sellerPromotion.conditionValue,
          }}
        />
      )
    }
    case OfferDiscountTypeEnumV2.TotalAmount:
    case OfferDiscountTypeEnumV2.TotalPercent: {
      return (
        <FormattedMessage
          id="seller.promotion.product.banner.info.minSameQuantity.total"
          values={{
            formattedDiscountAmount:
              getFormattedPromotionDiscountedAmountOrPercentage(
                sellerPromotion,
                intl,
              ),
            targetItemCount: sellerPromotion.conditionValue,
          }}
        />
      )
    }
    default: {
      return null
    }
  }
}

export const renderProductSellerPromotionInfoBannerMessage = ({
  footnoteNumber,
  intl,
  sellerName,
  sellerPromotion,
}: {
  footnoteNumber?: number
  intl: IntlShape
  sellerName?: string
  sellerPromotion: SellerPromotionV2
  tenant: TENANT
}): ReactNode => {
  switch (sellerPromotion.conditionType) {
    case OfferConditionTypeEnumV2.MinimumOrderValue: {
      return renderMinimumOrderValueMessage(sellerPromotion, intl, sellerName)
    }

    case OfferConditionTypeEnumV2.MinimumQuantity: {
      return renderMinQuantityMessage(sellerPromotion, intl, sellerName)
    }

    case OfferConditionTypeEnumV2.MinimumSameItemQuantity: {
      return renderMinQuantitySameItemMessage({
        footnoteNumber,
        intl,
        sellerName,
        sellerPromotion,
      })
    }

    default: {
      // older promotions do not contain "conditionType" and are implicitly of type "minimum order value"
      return (
        <ProductSellerPromotionInfoBannerMovMessage
          sellerName={sellerName}
          sellerPromotion={sellerPromotion}
        />
      )
    }
  }
}
